'use client';

import Script from 'next/script';
import { useEffect, useRef, useState } from 'react';

const PORTAL_ID = '2389934';
const FORM_ID = '00002065-0065-4d85-9bde-2b0bfa0b904e';
const CONTAINER_ID = 'newsroom-hs-form';
const CONTAINER_ID_MOBILE = 'newsroom-hs-form-mobile';

// py-16 = 64px (mobile), py-24 = 96px (lg+)
const PAGE_PADDING_LG = 86;
const PAGE_PADDING_SM = 56;
const SCROLLED_GAP = 14;
const SCROLL_THRESHOLD = 120;

const FOOTER_GAP = 16;

function useAdaptiveTop(sidebarRef) {
    const [state, setState] = useState({ top: 0, clamped: false });

    useEffect(() => {
        function compute() {
            const navBottom =
                document.querySelector('header')?.getBoundingClientRect().bottom ?? 80;
            const pagePadding = window.innerWidth >= 1024 ? PAGE_PADDING_LG : PAGE_PADDING_SM;
            const scrolled = window.scrollY > SCROLL_THRESHOLD;
            let desired = navBottom + (scrolled ? SCROLLED_GAP : pagePadding);

            const mainBottom = document.querySelector('main')?.getBoundingClientRect().bottom;
            if (mainBottom != null && sidebarRef.current) {
                const sidebarHeight = sidebarRef.current.offsetHeight;
                const maxTop = mainBottom - sidebarHeight - FOOTER_GAP;
                if (desired > maxTop) return { top: maxTop, clamped: true };
            }

            return { top: desired, clamped: false };
        }

        function update() {
            setState(compute());
        }

        update();
        window.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update, { passive: true });
        return () => {
            window.removeEventListener('scroll', update);
            window.removeEventListener('resize', update);
        };
    }, [sidebarRef]);

    return state;
}

function mountForm() {
    window.hbspt?.forms?.create({
        region: 'na1',
        portalId: PORTAL_ID,
        formId: FORM_ID,
        target: `#${CONTAINER_ID}`
    });
}

function mountFormMobile() {
    window.hbspt?.forms?.create({
        region: 'na1',
        portalId: PORTAL_ID,
        formId: FORM_ID,
        target: `#${CONTAINER_ID_MOBILE}`
    });
}

export function MobileNewsroomBottomSheet() {
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (window.hbspt?.forms) {
            mountFormMobile();
            return;
        }
        const interval = setInterval(() => {
            if (window.hbspt?.forms) {
                mountFormMobile();
                clearInterval(interval);
            }
        }, 200);
        return () => clearInterval(interval);
    }, []);

    return (
        <div
            className={`fixed inset-x-0 bottom-0 z-30 transition-transform duration-300 ease-in-out lg:hidden ${
                isOpen ? 'translate-y-0' : 'translate-y-[calc(100%-52px)]'
            }`}>
            <button
                onClick={() => setIsOpen((o) => !o)}
                aria-expanded={isOpen}
                aria-label={isOpen ? 'Collapse newsletter form' : 'Expand newsletter form'}
                className="flex h-[52px] w-full items-center justify-between rounded-t-2xl bg-blue-700 px-6 text-white shadow-lg">
                <div className="flex items-center gap-2">
                    <svg
                        className="size-4 shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}>
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                    </svg>
                    <span className="text-sm font-bold">Get dotCMS News</span>
                </div>
                <svg
                    className={`size-5 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                </svg>
            </button>
            <div className="max-h-[80vh] overflow-y-auto bg-white px-6 pb-8 pt-5 shadow-xl">
                <p className="mb-1 text-xs font-bold uppercase tracking-widest text-fuschia-700">
                    Stay Informed
                </p>
                <h3 className="mb-2 text-xl font-bold text-blue-700">Get dotCMS News</h3>
                <p className="mb-5 text-sm leading-relaxed text-blue-600">
                    Receive the latest product updates, press releases, and announcements directly
                    in your inbox.
                </p>
                <div id={CONTAINER_ID_MOBILE} />
            </div>
        </div>
    );
}

export function NewsroomSidebar() {
    const sidebarRef = useRef(null);
    const { top, clamped } = useAdaptiveTop(sidebarRef);

    useEffect(() => {
        if (window.hbspt?.forms) mountForm();
    }, []);

    return (
        <div
            ref={sidebarRef}
            className={`fixed z-20 w-[26rem] ${clamped ? '' : 'transition-[top] duration-300 ease-in-out'}`}
            style={{ top }}>
            <div className="rounded-2xl border border-blue-200 bg-white p-6 shadow-sm">
                <p className="mb-1 text-xs font-bold uppercase tracking-widest text-fuschia-700">
                    Stay Informed
                </p>
                <h3 className="mb-2 text-xl font-bold text-blue-700">Get dotCMS News</h3>
                <p className="mb-5 text-sm leading-relaxed text-blue-600">
                    Receive the latest product updates, press releases, and announcements directly
                    in your inbox.
                </p>
                <Script
                    src="//js.hsforms.net/forms/embed/v2.js"
                    strategy="lazyOnload"
                    onReady={mountForm}
                />
                <div id={CONTAINER_ID} />
            </div>
        </div>
    );
}
